import type { OrbitalElements } from "../math/orbital_elements";
import { meanAnomalyAt } from "../math/orbital_elements";
import type { StateVector } from "../math/state_vector";
import {
  elementsToState,
  solveKeplerEquation,
  eccentricAnomalyToTrueAnomaly,
} from "../math/orbital_math";

/**
 * Propagates Keplerian orbits by advancing the mean anomaly.
 */

/**
 * Propagate orbital elements forward in time and return new state vector.
 *
 * @param elements Current orbital elements
 * @param gm Gravitational parameter (km^3/s^2)
 * @param newTimeJD Target Julian Date
 * @returns State vector at new time
 */
export function propagate(
  elements: OrbitalElements,
  gm: number,
  newTimeJD: number,
): StateVector {
  return elementsToState(elements, gm, newTimeJD);
}

/**
 * Update orbital elements to a new epoch (recompute mean anomaly at new time).
 */
export function updateEpoch(
  elements: OrbitalElements,
  gm: number,
  newEpochJD: number,
): OrbitalElements {
  const newM0 = meanAnomalyAt(elements, newEpochJD, gm);
  return {
    ...elements,
    meanAnomalyAtEpoch: newM0,
    epoch: newEpochJD,
  };
}

/**
 * Compute the true anomaly at a given time.
 */
export function trueAnomalyAt(
  elements: OrbitalElements,
  gm: number,
  jd: number,
): number {
  const M = meanAnomalyAt(elements, jd, gm);
  const E = solveKeplerEquation(M, elements.eccentricity);
  return eccentricAnomalyToTrueAnomaly(E, elements.eccentricity);
}
